/**
 * NSW Cookie Consent Integration
 *
 * This module initializes the NSW Design System cookie consent banner
 * with configuration from SilverStripe CMS and provides GTM consent mode integration
 */

import CookieConsentClass from 'nsw-design-system/src/components/cookie-consent/cookie-consent'
import * as CookieConsentAPI from 'vanilla-cookieconsent'

/**
 * Initialize NSW Cookie Consent with configuration from CMS
 *
 * @param {Object} config - Cookie consent configuration object from PHP
 */
export function initCookieConsent(config) {
    if (!config) {
        console.warn('Cookie consent configuration not provided');
        return;
    }

    // vanilla-cookieconsent calls crypto.subtle.digest() during init, which
    // requires a secure context (HTTPS or localhost/127.0.0.1). Plain HTTP on
    // any other hostname leaves window.crypto.subtle undefined and the library
    // throws "Cannot read properties of undefined (reading 'digest')" before
    // we get a chance to render anything. Bail with a clear warning rather
    // than crashing the rest of the page's JS.
    if (!window.isSecureContext || !window.crypto?.subtle) {
        console.warn(
            'Cookie consent skipped: a secure context (HTTPS or localhost) is required. ' +
            'window.isSecureContext=' + window.isSecureContext +
            ', crypto.subtle=' + (window.crypto?.subtle ? 'present' : 'undefined')
        );
        return;
    }

    console.log('Initializing NSW Cookie Consent...');

    // Ensure NSW namespace exists
    if (!window.NSW) {
        window.NSW = {};
    }

    // Add CookieConsent to NSW namespace (required by the component)
    window.NSW.CookieConsent = CookieConsentClass;

    // Make vanilla-cookieconsent API available globally for GTM bridge and cookie consent
    window.CookieConsentApi = CookieConsentAPI;

    // For returning visitors the server strips consentBanner/preferencesDialog
    // from the config (PERF: cookie-consent), but NSW.CookieConsent's
    // constructor unconditionally renders the banner and crashes without them.
    // Skip the NSW UI init in that case; vanilla-cookieconsent + GTM bridge
    // still gets set up below.
    let consent = null;
    if (config.consentBanner && config.preferencesDialog) {
        consent = new window.NSW.CookieConsent(config);
    }

    // Set up GTM consent mode integration
    // Wait for vanilla-cookieconsent to be fully initialized before setting up event listeners
    window.addEventListener('cc:onModalReady', () => {
        setupGTMConsentBridge(config);
    }, { once: true });

    console.log('NSW Cookie Consent initialized');

    return consent;
}

/**
 * Set up Google Tag Manager consent mode integration
 * Maps NSW cookie consent categories to GTM consent types
 *
 * @param {Object} config - Cookie consent configuration
 */
function setupGTMConsentBridge(config) {
    if (!config.categories) {
        return;
    }

    // Wait for vanilla-cookieconsent to be ready
    if (!window.CookieConsentApi) {
        console.warn('CookieConsentApi not available - GTM consent bridge not initialized');
        return;
    }

    console.log('Setting up GTM consent bridge...');

    // Set default consent state (denied for all)
    const defaultConsent = {};
    Object.keys(config.categories).forEach(categoryKey => {
        const category = config.categories[categoryKey];
        if (category.gtmConsentTypes && category.gtmConsentTypes.length > 0) {
            category.gtmConsentTypes.forEach(gtmType => {
                // Default to denied unless it's a readOnly (necessary) category
                defaultConsent[gtmType] = category.readOnly ? 'granted' : 'denied';
            });
        }
    });

    // Set default consent in GTM
    if (window.gtag && Object.keys(defaultConsent).length > 0) {
        console.log('Setting default GTM consent:', defaultConsent);
        window.gtag('consent', 'default', defaultConsent);
    }

    // Listen for consent changes using DOM events
    // vanilla-cookieconsent dispatches 'cc:onConsent' event when consent changes
    window.addEventListener('cc:onConsent', (event) => {
        console.log('Consent changed, updating GTM consent');
        const acceptedCategories = window.CookieConsentApi.getUserPreferences()?.acceptedCategories;
        if (acceptedCategories) {
            updateGTMConsent(acceptedCategories, config.categories);
        }
    });

    // Also check for existing consent on page load
    const acceptedCategories = window.CookieConsentApi.getUserPreferences()?.acceptedCategories;
    if (acceptedCategories) {
        console.log('Found existing consent preferences, updating GTM');
        updateGTMConsent(acceptedCategories, config.categories);
    }

    console.log('GTM consent bridge initialized');
}

/**
 * Update GTM consent state based on user's cookie preferences
 *
 * @param {Array} acceptedCategories - Array of accepted category keys
 * @param {Object} configCategories - Category configuration from CMS
 */
function updateGTMConsent(acceptedCategories, configCategories) {
    if (!window.gtag || !configCategories) {
        return;
    }

    const consentUpdate = {};

    // Go through each category and update GTM consent
    Object.keys(configCategories).forEach(categoryKey => {
        const category = configCategories[categoryKey];

        if (category.gtmConsentTypes && category.gtmConsentTypes.length > 0) {
            const isAccepted = acceptedCategories.includes(categoryKey) || category.readOnly;

            category.gtmConsentTypes.forEach(gtmType => {
                consentUpdate[gtmType] = isAccepted ? 'granted' : 'denied';
            });
        }
    });

    if (Object.keys(consentUpdate).length > 0) {
        console.log('Updating GTM consent:', consentUpdate);
        window.gtag('consent', 'update', consentUpdate);
    }
}

/**
 * Activate scripts with data-category attributes based on consent
 * This is called automatically by vanilla-cookieconsent when consent changes
 *
 * Scripts with type="text/plain" and data-category="xxx" will be activated
 * when the user consents to that category.
 */
export function activateConsentedScripts() {
    // This is handled automatically by vanilla-cookieconsent
    // Scripts with type="text/plain" data-category="categoryKey" are activated when consent is given
    console.log('Scripts will be activated automatically by vanilla-cookieconsent');
}
